import { ConnectionDescriptor, CreatedSubmissionRequest, SubmissionDetail } from '../../Models/Api/strongbox.financialportal';
import { AppCookieData } from '../../Models/CookieData';
import { PortalCreateSubmissionRequestOptions } from '../../Models/PortalCreateSubmissionRequestOptions';
import { AttributedParagraphSet } from '../../Utils/AttributedParagraph';
import { StrongboxConnectionDescriptor } from '../../Utils/ConnectStrongbox';
import { PartialExcept } from '../../Utils/TypescriptUtils';
import { ConnectorType, LinkMode } from '../ImportFinancials';

export enum NewDataCollectionParametersActions {
    SetOptions = 'NDCP/SetOptions',
    Initialize = 'NDCP/Initialize',
    BeginInitializeFromURL = 'NDCP/BeginInitializeFromURL',
    EndInitializeFromURL = 'NDCP/EndInitializeFromURL',
    InitializeCollectionGroupings = 'NDCP/InitializeCollectionGroupings',
    BeginCurrentSubmissionRequest = 'NDCP/BeginCurrentSubmissionRequest',
    SetSubmissionRequestCreated = 'NDCP/SetSubmissionRequestCreated',
    SetSubmissionRequestError = 'NDCP/SetSubmissionRequestError',
    SetCreatingSubmission = 'NDCP/SetCreatingSubmission',
    SetSubmissionError = 'NDCP/SetSubmissionError',
    ClearCurrentSubmission = 'NDCP/ClearCurrentSubmission',
    CompleteCurrentSubmission = 'NDCP/CompleteCurrentSubmission',
    SetSelectedAccountingPackage = 'NDCP/SetSelectedAccountingPackage',
    SetCurrentSubmission = 'NDCP/SetCurrentSubmission',
    SetConnectionDescriptor = 'NDCP/SetConnectionDescriptor',
    SetConnectEndpoint = 'NDCP/SetConnectEndpoint',
    SetAccountingSystemConnectionState = 'NDCP/SetAccountingSystemConnectionState',
    SetExistingConnection = 'NDCP/SetExistingConnection',
}

export enum AccountingSystemConnectionState {
    idle = 'idle',
    connecting = 'connecting',
    connected = 'connected',
    error = 'error',
    aborted = 'aborted',
}

export interface IConnectionError {
    msg: string;
    detailedMsg: string;
    errorCode?: string;
}

export interface ISetOptionsAction {
    readonly type: NewDataCollectionParametersActions.SetOptions;
    readonly options: PortalCreateSubmissionRequestOptions;
    readonly fiscalYearsToCollect: number;
}

export interface IInitialize {
    readonly type: NewDataCollectionParametersActions.Initialize;
    readonly appData: AppCookieData;
}

export interface IInitializeCollectionGroupings {
    readonly type: NewDataCollectionParametersActions.InitializeCollectionGroupings;
    readonly collectionGroupDescriptions: AttributedParagraphSet;
    readonly dynamicTenantSelectionEnabled: boolean;
}

export interface IBeginInitializeFromURL {
    readonly type: NewDataCollectionParametersActions.BeginInitializeFromURL;
    readonly url: string;
}

export interface ISetSelectedAccountingPackage {
    readonly type: NewDataCollectionParametersActions.SetSelectedAccountingPackage;
    readonly accountingPackage: string | null;
}

export interface ISetCurrentSubmission {
    readonly type: NewDataCollectionParametersActions.SetCurrentSubmission;
    readonly submissionId: string;
}

// If there is an error retrieving options, optionsId should be set to -1.

export interface IEndInitializeFromURL {
    readonly type: NewDataCollectionParametersActions.EndInitializeFromURL;
    readonly url: string;
    readonly optionsId: number | undefined;
    readonly options: PortalCreateSubmissionRequestOptions | undefined;
    readonly initializationException?: any;
}

export interface ISetConnectionDescriptor {
    readonly type: NewDataCollectionParametersActions.SetConnectionDescriptor;
    readonly descriptor: StrongboxConnectionDescriptor
}

// Records the resolved connect URL (the endpoint the popup was navigated to) on the current submission.
export interface ISetConnectEndpoint {
    readonly type: NewDataCollectionParametersActions.SetConnectEndpoint;
    readonly connectEndpoint: string;
}

export enum ExistingConnectionLookupState {
    pending = 'pending',   // not yet checked / lookup in flight
    notFound = 'notFound', // checked, none found
    found = 'found',       // checked, connection available
}

export interface ICurrentSubmission {
    dataCollectionSubmissionRequest: IDataCollectionSubmissionRequest;
    submissionRequest: CreatedSubmissionRequest | null;
    submissionState: SubmissionState;
    submission: PartialExcept<SubmissionDetail, 'id'> | null;
    accountingPackage: string | null;
    accountingSystemConnectionState: AccountingSystemConnectionState;
    connectionError: IConnectionError | undefined;
    existingConnectionLookupState: ExistingConnectionLookupState;
    existingConnection: ConnectionDescriptor | null;
    // The fully-resolved connect URL the popup was navigated to. Surfaced so the universal collector
    // "complete in the new window / copy link" modal can offer a manual fallback link. Null until the
    // connect descriptor has been retrieved.
    connectEndpoint: string | null;
}

export interface ISetAccountingSystemConnectionState {
    readonly type: NewDataCollectionParametersActions.SetAccountingSystemConnectionState;
    readonly connectionState: AccountingSystemConnectionState;
    readonly connectionError?: IConnectionError;
}

// Records the result of the existing-connection lookup performed when an accounting package is selected.
// accountingPackage is the package the lookup ran for; the reducer discards the result if it no longer
// matches the currently-selected package (the user switched packages while the lookup was in flight).
// connection === null means "checked, none found" (a normal, non-error outcome).
export interface ISetExistingConnection {
    readonly type: NewDataCollectionParametersActions.SetExistingConnection;
    readonly accountingPackage: string;
    readonly connection: ConnectionDescriptor | null;
}


export interface IDataCollectionSubmissionRequest {
    workspaceId: string;
    workspaceName: string;
    linkMode: LinkMode;
    connectorType: ConnectorType;
    options: PortalCreateSubmissionRequestOptions;
    // Who is driving the direct connect. Only the borrower (secure-URL) flow reuses an existing connection
    // (reuse/disconnect); the lender "new data collection → connect directly" flow always creates a new one,
    // so it skips the existing-connection lookup and the confirmation modal always shows just "Connect".
    // Undefined is treated as 'lender' (always new).
    sourceFlow?: 'lender' | 'borrower';
    // The ANONYMOUS borrower (went straight to /submission, no shared-link cookie) only holds a
    // PortalSubmissionSessionCookie granting Submissions.Create + Connections.Create on the workspace. It is
    // NOT authorized for POST /SubmissionRequests or GET /connections (401), so its flow skips both: no
    // submission request is created and the existing-connection lookup is bypassed. The submission is created
    // directly (no requestId), mirroring the V1 borrower flow. Undefined/false = the request-based path.
    isAnonymous?: boolean;
}

export enum SubmissionState {
    creatingRequest = 'creatingRequest',
    requestCreated = 'requestCreated',
    creatingSubmission = 'creatingSubmission',
    submissionCreated = 'submissionCreated',
    submissionError = 'submissionError',
    requestError = 'requestError',
}

// Begins (and fully resets) the current submission. This is the ONLY action that rebuilds the
// currentSubmission object. 
export interface IBeginCurrentSubmissionRequest {
    readonly type: NewDataCollectionParametersActions.BeginCurrentSubmissionRequest;
    readonly dataCollectionSubmissionRequest: IDataCollectionSubmissionRequest;
}

// Records the submission request created by the back end and advances state to requestCreated.
// Merge: preserves every other field on currentSubmission.
export interface ISetSubmissionRequestCreated {
    readonly type: NewDataCollectionParametersActions.SetSubmissionRequestCreated;
    // null for the anonymous borrower flow, which advances to requestCreated without creating a server-side
    // submission request (it is not authorized to). The reducer already types submissionRequest as nullable.
    readonly submissionRequest: CreatedSubmissionRequest | null;
}

// State-only transition to requestError. Merge.
export interface ISetSubmissionRequestError {
    readonly type: NewDataCollectionParametersActions.SetSubmissionRequestError;
}

// State-only transition to creatingSubmission. Merge: critically preserves accountingPackage,
// submissionRequest, submission and connection state set in earlier steps.
export interface ISetCreatingSubmission {
    readonly type: NewDataCollectionParametersActions.SetCreatingSubmission;
}

// State-only transition to submissionError. Merge.
export interface ISetSubmissionError {
    readonly type: NewDataCollectionParametersActions.SetSubmissionError;
}

// Tears down the current submission. Idempotent: always sets currentSubmission to null, never throws.
export interface IClearCurrentSubmission {
    readonly type: NewDataCollectionParametersActions.ClearCurrentSubmission;
}

// Wraps up a finished submission flow: clears currentSubmission (like ClearCurrentSubmission) and
// bumps dataCollectionSubmissionsCount so consumers can tell a submission was completed. Idempotent.
export interface ICompleteCurrentSubmission {
    readonly type: NewDataCollectionParametersActions.CompleteCurrentSubmission;
}

export type KnownAction =
    ISetOptionsAction |
    IInitialize |
    IInitializeCollectionGroupings |
    IBeginInitializeFromURL |
    IEndInitializeFromURL |
    IBeginCurrentSubmissionRequest |
    ISetSubmissionRequestCreated |
    ISetSubmissionRequestError |
    ISetCreatingSubmission |
    ISetSubmissionError |
    IClearCurrentSubmission |
    ICompleteCurrentSubmission |
    ISetSelectedAccountingPackage |
    ISetCurrentSubmission |
    ISetConnectionDescriptor |
    ISetConnectEndpoint |
    ISetAccountingSystemConnectionState |
    ISetExistingConnection;
